/* eslint-disable import/order */
import { immutableSelector } from '@/utils'
import { InputNumber, Upload } from '@watchface/components'
import T from '@watchface/components/I18n'
import { ALIGN_H, ALIGN_V } from '@watchface/constants'
import { VERTEX_DOT_LINE_STYLE } from '@watchface/constants/graph'
import { RADIO_STYLE, SELECT_STYLE } from '@watchface/constants/style'
import { getDefaultLangField, getOriginLang } from '@watchface/utils'
import { renderCells } from '@watchface/utils/render'
import { Radio, Select } from 'antd'
import Immutable from 'immutable'
import React, { PureComponent } from 'react'

const { Option, OptGroup } = Select
const noop = () => {} // eslint-disable-line

const LANG_MAPPER = {
  all: {
    label: <T id="thumbnail_all" />,
    field: 'all'
  },
  zh: {
    label: <T id="thumbnail_zh" />,
    field: 'zh'
  },
  'zh-Hant': {
    label: <T id="thumbnail_zh-hant" />,
    field: 'zh-Hant'
  },
  sc: {
    label: <T id="thumbnail_zh&zh-hant" />,
    field: 'zh'
  },
  en: {
    label: <T id="thumbnail_en" />,
    field: 'en'
  }
}

class TemperatureNumberWithRect extends PureComponent {
  static keyMap = {}

  static renderLayer = (param) => {
    const keyMap = renderCells({
      com: TemperatureNumberWithRect,
      ...param
    })
    TemperatureNumberWithRect.keyMap = Object.assign(TemperatureNumberWithRect.keyMap, keyMap)
  }

  static getCellDatas = ({ parentKey, childKey, dialType, allData, data, allAssets, constMap }) => {
    const cells = {
      imageEntity: {},
      vertexEntity: {}
    }
    const images = TemperatureNumberWithRect.getImages({
      parentKey,
      childKey,
      dialType,
      allData,
      allAssets,
      constMap
    })
    images.forEach((image) => {
      cells.imageEntity[image.key] = image
    })

    const vertex = TemperatureNumberWithRect.getVertex({ data, childKey })

    if (vertex) {
      cells.vertexEntity[vertex.key] = vertex
    }

    return cells
  }

  static getImages = ({ parentKey, childKey, dialType, allData, allAssets, constMap, renderLayerByLang }) => {
    const after = constMap.getIn([childKey, 'after']) || Immutable.List()
    let updateKey = childKey

    for (let i = 0; i < after.size; i += 1) {
      const afterKey = after.get(i)
      const combin = allData.getIn([parentKey, 'children', afterKey, 'combin'])

      if (combin !== 'follow') break

      updateKey = afterKey
    }
    const imageArr = TemperatureNumberWithRect.getImgArrAfterAlign({
      allData,
      allAssets,
      key: updateKey,
      parentKey,
      constMap,
      dialType,
      renderLayerByLang,
    })

    const sepImage = TemperatureNumberWithRect.getSepImage({ allData, parentKey, childKey, constMap })
    if (sepImage) {
      imageArr.push(sepImage)
    }
    return imageArr
  }

  static getVertex = ({ data, childKey }) => {
    const rectPath = ['text', 'image', 'rectangle']
    const { position = Immutable.Map(), width = 0, height = 0 } = immutableSelector(data.getIn(rectPath) || Immutable.Map())
    const { x = 0, y = 0 } = immutableSelector(position || Immutable.Map())

    return {
      key: `${childKey}-rect`,
      type: 'rect',
      left: x,
      top: y,
      width,
      height,
      fill: 'transparent',
      ...VERTEX_DOT_LINE_STYLE
    }
  }

  static getSepImage = ({ allData, parentKey, childKey, constMap }) => {
    const showSeparatorPos = TemperatureNumberWithRect.judgeShowSeparatorPos(allData, parentKey, childKey, constMap)
    if (showSeparatorPos) {
      const separator = allData.getIn([parentKey, 'children', childKey, 'separator']) || Immutable.Map()
      const { position = Immutable.Map(), image = Immutable.Map() } = immutableSelector(separator)
      const { x = 0, y = 0 } = position
      const url = image.get('url')

      if (url) {
        return {
          key: `${childKey}-separator`,
          texture: url,
          left: x,
          top: y
        }
      }
    }
    return null
  }

  static getImgArrAfterAlign = ({ allData = Immutable.Map(), allAssets = Immutable.Map(), key, parentKey, constMap = Immutable.Map(), dialType, renderLayerByLang }) => {
    const data = TemperatureNumberWithRect.getForefrontData(parentKey, key, allData, constMap)
    const rectangle = data.getIn(['text', 'image', 'rectangle']) || Immutable.Map()
    const rectAlign = data.getIn(['text', 'rect_align']) || Immutable.Map()
    const interval = data.getIn(['text', 'interval']) || 0
    const { horizontal = 'left', vertical = 'top' } = immutableSelector(rectAlign)
    const { width = 1, height = 1 } = immutableSelector(rectangle)

    const imageArr = TemperatureNumberWithRect.getImageArr({
      allData,
      allAssets,
      key,
      parentKey,
      constMap,
      dialType,
      renderLayerByLang,
    })

    const existImageArr = imageArr
    const areaWidth = existImageArr.reduce((pre, curr, index) => {
      const { width = 0 } = immutableSelector(curr)
      const newInterval = index === existImageArr.size - 1 ? 0 : interval

      return pre + width + newInterval
    }, 0)
    const areaHeight = existImageArr.reduce((pre, curr) => {
      const { height = 0 } = immutableSelector(curr)

      return pre > height ? pre : height
    }, 0)

    let paddingLeft = 0
    let paddingTop = 0

    switch (horizontal) {
      case ALIGN_H.RIGHT:
        paddingLeft = width - areaWidth
        break
      case ALIGN_H.CENTER:
        paddingLeft = (width - areaWidth) / 2
        break
      case ALIGN_H.LEFT:
      default:
        paddingLeft = 0
    }

    switch (vertical) {
      case ALIGN_V.CENTER:
        paddingTop = (height - areaHeight) / 2
        break
      case ALIGN_V.BOTTOM:
        paddingTop = height - areaHeight
        break
      case ALIGN_V.TOP:
      default:
        paddingTop = 0
    }

    if (width < areaWidth) paddingLeft = 0
    if (height < areaHeight) paddingTop = 0

    return imageArr
      .map((item) => {
        const { left, top, url } = immutableSelector(item)
        return item.merge(
          Immutable.Map({
            left: left + paddingLeft,
            top: top + paddingTop,
            texture: url
          })
        )
      })
      .toJS()
  }

  // 获取最前面被跟随模块的数据
  static getForefrontData = (parentKey, childKey, allData = Immutable.Map(), constMap = Immutable.Map()) => {
    const data = allData.getIn([parentKey, 'children', childKey]) || Immutable.Map()
    const nowCombin = data.get('combin') || ''

    // 终止条件为当前的 data combin 为 single
    if (nowCombin !== 'follow') return data

    const before = constMap.getIn([childKey, 'before'])
    const beforeKey = before.get(0)

    return TemperatureNumberWithRect.getForefrontData(parentKey, beforeKey, allData, constMap)
  }

  static getIndexFromNumber = (value, stepCount, num, padding_zero) => {
    if (typeof value !== 'number') return []

    const [integer, decimal] = String(value).split('.')

    /* eslint-disable */
    if (padding_zero) {
      // 需要先将小数补全 再补全前面
      return value === 0 && stepCount
        ? `${value}.${'0'.repeat(stepCount)}`.padStart(num, '0').split('')
        : decimal
        ? (`${integer}`.padStart(num - stepCount - 1, '0') + '.' + `${decimal}`.padEnd(stepCount, '0')).split('')
        : `${integer}`.padStart(num, '0').split('')
    } else {
      // 不需要补零的时候不用管整数部分
      return value === 0 && stepCount
        ? `${value}.${'0'.repeat(stepCount)}`.split('')
        : decimal
        ? (`${integer}.` + `${decimal || ''}`.padEnd(stepCount, '0')).split('')
        : `${integer}`.split('')
    }
    /* eslint-enable */
  }

  static getImageArr = ({ allData = Immutable.Map(), allAssets = Immutable.Map(), key, parentKey, renderLayerByLang, constMap = Immutable.Map(), dialType }) => {
    let imageArr = Immutable.List()
    const basePath = [parentKey, 'children', key]
    const assets = allAssets.getIn([dialType, ...basePath]) || Immutable.Map()
    const { languages = 'all' } = immutableSelector(assets)
    const defaultLang = getDefaultLangField(languages, LANG_MAPPER)
    const previewLang = renderLayerByLang || assets.get('previewLang') || defaultLang
    const before = constMap.getIn([key, 'before'])
    const stepCount = constMap.getIn([key, 'stepCount'])
    const supportImage = constMap.getIn([key, 'supportImage'])
    const nowData = allData.getIn(basePath)
    let foreFrontData = nowData
    const combin = nowData.get('combin')

    if (Immutable.List.isList(before) && combin === 'follow') {
      const frontKey = before.get(0)

      before.some((beforeKey) => {
        const beforeCombin = allData.getIn([parentKey, 'children', beforeKey, 'combin'])

        if (beforeCombin !== 'follow') {
          foreFrontData = allData.getIn([parentKey, 'children', beforeKey])
          return true
        }
        return false
      })
      if (frontKey) {
        // 递归获取前面的图片
        const frontImageArr = TemperatureNumberWithRect.getImageArr({
          allData,
          allAssets,
          key: frontKey,
          parentKey,
          constMap,
          dialType,
          renderLayerByLang,
        })
        imageArr = frontImageArr.concat(imageArr)
      }
    }

    // 获取当前 key 数据
    const { text: currentText = Immutable.Map(), separator = Immutable.Map() } = immutableSelector(nowData)
    const { padding_zero, image: currentImage = Immutable.Map() } = immutableSelector(currentText)
    const { unit_images: unitImages = Immutable.List(), negative_image: negativeImage = Immutable.Map() } = immutableSelector(currentImage)
    const separatorImage = separator.get('image')

    // 获取最终被跟随 key 数据
    const { text = Immutable.Map() } = immutableSelector(foreFrontData)
    const { image = Immutable.Map(), interval = 0, is_character = false } = immutableSelector(text)
    // supportImage 为 true 表示支持选择图片的展示形式为数字或者图片
    // is_character 为 0 表示当前选择数字 1 表示当前选择了图片
    const isImage = supportImage && is_character
    // 如果当前为图片展示时 仅展示一位
    let value = assets.getIn(['num', 'value']) || (isImage ? 1 : 0)
    // 数值总位数
    const valueCount = constMap.getIn([key, 'valueCount'])
    const newValueCount = value > 0 ? valueCount : (valueCount || 0) + 1
    // // 获取需要展示图片的数量
    // function getShowImageCount ({ supportImage, is_character, assets }) {

    // }

    const {
      font_images = Immutable.List(),
      follow_image: followImage = Immutable.Map(),
      rectangle = Immutable.Map(),
      point_image: pointImage = Immutable.Map()
    } = immutableSelector(image)
    const { position = Immutable.Map() } = immutableSelector(rectangle)
    const { x = 0, y = 0 } = immutableSelector(position)
    const dotIndex = String(value).indexOf('.')
    const showSeparatorPos = TemperatureNumberWithRect.judgeShowSeparatorPos(allData, parentKey, key, constMap)
    let fontImagesIndex = 0

    if (dotIndex > -1) {
      // 保留 stepCount 位小数
      value = String(value).slice(0, dotIndex + stepCount + 1)
    }

    if (isImage) {
      fontImagesIndex = font_images.findIndex((item) => item.get('language') === previewLang)
    }

    const { images: fontImages = Immutable.List() } = immutableSelector(fontImagesIndex === -1 ? Immutable.Map() : font_images.get(fontImagesIndex))
    const indexes = TemperatureNumberWithRect.getIndexFromNumber(Number(value), stepCount, newValueCount, padding_zero)
    // 数字图片位数（包括小数点）
    const numCount = indexes.length > newValueCount ? newValueCount : indexes.length

    // 插入图标
    if (combin !== 'follow') {
      const { url } = immutableSelector(followImage)

      if (url) {
        imageArr = imageArr.push(
          followImage.merge(
            Immutable.Map({
              key: `${key}-followIcon`,
              left: x,
              top: y,
              texture: url
            })
          )
        )
      }
    }

    // 插入数字图片
    for (let i = 0; i < numCount; i += 1) {
      const index = indexes[i]
      let img = Immutable.Map()

      if (index === '.') {
        img = pointImage || Immutable.Map()
      } else if (index === '-') {
        img = negativeImage || Immutable.Map()
      } else {
        img = fontImages.get(index) || Immutable.Map()
      }

      const beforeImg = imageArr.last()

      if (beforeImg) {
        const { left = 0, top = 0, width = 0 } = immutableSelector(beforeImg)

        img = img.set('left', left + width + interval).set('top', top)
      } else {
        img = img.set('left', x).set('top', y)
      }

      const { url } = immutableSelector(img)

      img = img.set('key', `${key}-fontImage${i}`).set('texture', url)
      if (url) {
        imageArr = imageArr.push(img)
      }
    }

    // 插入单位图片
    if (unitImages) {
      const lastImage = imageArr.last() || Immutable.Map()

      const { left = 0, top = 0, width = 0 } = immutableSelector(lastImage)
      let unitImage = unitImages.getIn([0, 'image']) || Immutable.Map()

      unitImage = unitImage.merge(
        Immutable.Map({
          key: `${key}-unitImage`,
          left: left + width + interval,
          top,
          texture: unitImage.get('url')
        })
      )
      if (unitImage.get('url')) {
        imageArr = imageArr.push(unitImage)
      }
    }

    // 插入间隔符图片
    if (!showSeparatorPos && Immutable.Map.isMap(separatorImage)) {
      const lastImage = imageArr.last() || Immutable.Map()
      const { left = 0, top = 0, width = 0 } = immutableSelector(lastImage)

      if (separatorImage.get('url')) {
        imageArr = imageArr.push(
          separatorImage.merge(
            Immutable.Map({
              key: `${key}-separator`,
              left: left + width + interval,
              top,
              texture: separatorImage.get('url')
            })
          )
        )
      }
    }

    return imageArr
  }

  // 跟随及被跟随模块禁止隐藏
  static judgeShowSeparatorPos = (allData, parentKey, childKey, constMap) => {
    const isImmutableMap = Immutable.Map.isMap

    if (!isImmutableMap(allData) || !isImmutableMap(constMap)) return false

    const combin = allData.getIn([parentKey, 'children', childKey, 'combin'])
    const afterKey = constMap.getIn([childKey, 'after', 0])
    const afterCombin = allData.getIn([parentKey, 'children', afterKey, 'combin'])

    return !(combin === 'follow' || afterCombin === 'follow')
  }

  componentDidUpdate(prevProps) {
    if (prevProps !== this.props) {
      const { allData: preAllData, data: preData } = prevProps
      const { allData, data, parentKey, childKey } = this.props
      const font_images_path = ['text', 'image', 'font_images']
      const combin_path = [parentKey, 'children', childKey, 'combin']
      const fontImages = data.getIn(font_images_path) || Immutable.List()
      const preFontImages = preData.getIn(font_images_path) || Immutable.List()
      const combin = allData.getIn(combin_path)
      const preCombin = preAllData.getIn(combin_path)
      const follow = preCombin !== combin && combin === 'follow'

      if (follow || preFontImages !== fontImages) {
        const direct = combin === 'follow' ? 'before' : 'after'
        this.syncCombinFontImages(data, direct)
      }
      TemperatureNumberWithRect.renderLayer(this.props)
    }
  }

  handleChange = (path, prop, isAssetsValue, val) => {
    let value = val
    const {
      data,
      onChange = noop,
      assets = Immutable.Map(),
      onAssetsChange = noop,
      childKey,
      dialType,
      allData,
      parentKey,
      allAssets,
      changeCallback
    } = this.props
    const rectProps = ['width', 'height', 'x', 'y']
    const isChangeRect = path.indexOf('rectangle') > -1 && rectProps.indexOf(prop) > -1
    let newData = data
    let newAssets = assets
    let newAllAssets = allAssets

    if (prop === 'is_character') {
      value = !!value
      newAssets = newAssets.setIn(['num', 'value'], value ? 1 : 0).set('alreadyImageKey', '')
      // 图片类型 和 （补零 和 间距 和 图片）互斥 清除数据
      newData = newData
        .setIn([...path, 'padding_zero'], false)
        .setIn([...path, 'interval'], 0)
        .setIn([...path, 'image', 'font_images'], Immutable.List())
    }

    newData = newData.setIn([...path, prop], value)

    const rootPath = [parentKey, 'children']

    if (isAssetsValue) {
      newAssets = newAssets.setIn([...path, prop], value)
    }

    if (prop === 'combin') {
      this.changeAssets('alreadyImageKey', '')

      const paths = [
        {
          path: ['text', 'image', 'position'],
          defaultValue: Immutable.Map({ x: 0, y: 0 })
        },
        {
          path: ['text', 'interval'],
          defaultValue: 0
        },
        {
          path: ['text', 'align'],
          defaultValue: 'left'
        },
        {
          path: ['text', 'is_character'],
          defaultValue: false
        }
      ]

      if (value === 'follow') {
        // 跟随时删除部分参数
        paths.forEach(({ path }) => {
          newData = newData.deleteIn(path)
        })
      } else {
        // 独立时恢复默认值
        paths.forEach(({ path, defaultValue }) => {
          newData = newData.setIn(path, defaultValue)
        })

        newData = newData.setIn(['text', 'image', 'font_images'], Immutable.List())
      }
    }

    const changeViewProp = [
      'value',
      'x',
      'y',
      'padding_zero',
      'align',
      'combin',
      'interval',
      'image',
      'point_image',
      'is_character',
      'separator',
      'horizontal',
      'vertical'
    ]

    newAllAssets = allAssets.setIn([dialType, ...rootPath, childKey], newAssets)

    if (isChangeRect) {
      this.updatePreview(newData, newAllAssets)
    } else if (changeViewProp.includes(prop)) {
      this.updatePreview(newData, newAllAssets)
    }

    onAssetsChange([dialType, ...rootPath, childKey], newAssets)
    onChange([...rootPath, childKey], newData)

    const newAllData = allData.setIn([...rootPath, childKey], newData)

    changeCallback && changeCallback({ allData: newAllData, allAssets: newAllAssets })
  }

  /**
   * 更新页面图片显示
   *
   * @param {Immutable.Map} newData
   * @param {Immutable.Map} newAllAssets
   */
  updatePreview = (newData, newAllAssets) => {
    const { childKey, dialType, allData, constMap, parentKey, renderLayerByLang } = this.props
    const rootPath = [parentKey, 'children']
    const newAllData = allData.setIn([...rootPath, childKey], newData)
    let updateKey = childKey
    const after = constMap.getIn([childKey, 'after']) || Immutable.List()

    for (let i = 0; i < after.size; i += 1) {
      const afterKey = after.get(i)
      const combin = allData.getIn([...rootPath, afterKey, 'combin'])

      if (combin !== 'follow') break

      updateKey = afterKey
    }

    const imgArr = TemperatureNumberWithRect.getImgArrAfterAlign({
      allData: newAllData,
      allAssets: newAllAssets,
      parentKey,
      key: updateKey,
      constMap,
      dialType,
      renderLayerByLang,
    })
    console.log('imgArr', imgArr)
  }

  handleRadioChange = (e) => {
    const { value } = e.target

    this.resetFontImages()
    this.changeAssets('useImage', value)
    this.changeAssets('alreadyImageKey', '')
  }

  handleCheckChange = (path, prop, isAssetsValue, value, e) => {
    e.preventDefault()
    this.handleChange(path, prop, isAssetsValue, !value)
  }

  changeAssets = (key, value) => {
    const { parentKey, childKey, dialType, onAssetsChange } = this.props

    onAssetsChange([dialType, parentKey, 'children', childKey, key], value)
  }

  resetFontImages = () => {
    const { allAssets, parentKey, childKey, data, onChange } = this.props
    const font_images_path = ['text', 'image', 'font_images']
    const newData = data.setIn(font_images_path, Immutable.List())

    onChange([parentKey, 'children', childKey], newData)
    this.updatePreview(newData, allAssets)
  }

  getFontImageArr = (fontImages) => {
    return fontImages
      .reduce((pre, fontImage) => {
        const { images = Immutable.List() } = immutableSelector(fontImage)
        return pre.concat(
          images.map((image) => {
            const { url, width, height, name } = immutableSelector(image)
            return {
              image: url,
              width,
              height,
              name
            }
          })
        )
      }, Immutable.List())
      .toJS()
  }

  handleChangeImage = (path, prop, isAssetsValue, img) => {
    if (img) {
      this.handleChange(path, prop, isAssetsValue, Immutable.fromJS({ url: img.image, width: img.width, height: img.height }))
    } else {
      this.handleChange(path, prop, isAssetsValue, Immutable.fromJS({ url: '', width: 0, height: 0 }))
    }
  }

  handleChangeInvalidImage = (path, prop, isAssetsValue, img) => {
    if (img) {
      this.handleChange(path, prop, isAssetsValue, img.image)
    } else {
      this.handleChange(path, prop, isAssetsValue, '')
    }
  }

  handlePunchChangeImage = (updateUploadedImages = true, image, lang = 'all') => {
    const {
      childKey,
      dialType,
      changeCallback,
      data,
      onChange = noop,
      parentKey,
      namePath,
      onAssetsChange,
      assets,
      allAssets,
      allData,
      constMap
    } = this.props
    const alreadyImageKey = assets.get('alreadyImageKey') || ''
    const font_images_path = ['text', 'image', 'font_images']
    const font_images = data.getIn(font_images_path) || Immutable.List()
    const imgArr = Immutable.fromJS(
      image.map((i) => {
        const { width, height, image, name } = i
        return {
          url: image,
          width,
          height,
          name
        }
      })
    )
    const originLangImagesIndex = font_images.findIndex((item) => item.get('language') === lang)
    let newData = data
    let newAllAssets = allAssets

    if (imgArr.size === 0) {
      newAllAssets = newAllAssets.setIn([dialType, parentKey, 'children', childKey, 'alreadyImageKey'], '')
    }

    if (updateUploadedImages) {
      const defaultImageKey = `${dialType}@_@${parentKey}-${childKey}`
      const keyPath = (alreadyImageKey || defaultImageKey).split('@_@')
      const basePath = ['uploadedImages', ...keyPath]
      let imageName = newAllAssets.getIn([...basePath, 'name']) || namePath

      if (imgArr.size === 0) imageName = namePath

      const newUploadData = Immutable.fromJS({
        name: imageName,
        images: imgArr
      })

      newAllAssets = newAllAssets.setIn(basePath, newUploadData)
    }

    if (originLangImagesIndex > -1) {
      font_images_path.push(originLangImagesIndex)
      newData = data.setIn(
        font_images_path,
        Immutable.fromJS({
          language: lang,
          images: imgArr
        })
      )
    } else {
      newData = data.setIn(
        font_images_path,
        Immutable.fromJS([
          ...font_images,
          {
            language: lang,
            images: imgArr
          }
        ])
      )
    }

    onChange([parentKey, 'children', childKey], newData)

    const newAllData = allData.setIn([parentKey, 'children', childKey], newData)

    this.updatePreview(newData, newAllAssets)

    changeCallback && changeCallback({ allData: newAllData, allAssets: newAllAssets, constMap })
    onAssetsChange([], newAllAssets)
  }

  /**
   *  同步跟随和被跟随者的图片 List 数据
   *
   *  @param {Immutable.Map} data
   *  @param {string} direction
   */
  syncCombinFontImages = (data, direction = 'after') => {
    const { allData, parentKey, childKey, constMap = Immutable.Map(), onChange } = this.props
    const arr = constMap.getIn([childKey, direction]) || Immutable.List()

    if (!arr.size) return

    const fontImagePath = ['text', 'image', 'font_images']

    if (direction === 'after') {
      try {
        const baseImages = data.getIn(fontImagePath)

        for (let i = 0; i < arr.size; i += 1) {
          const key = arr.get(i)
          const crtData = allData.getIn([parentKey, 'children', key])
          const combin = crtData.get('combin')

          if (combin === 'follow') {
            const newData = crtData.setIn(fontImagePath, baseImages)

            onChange([parentKey, 'children', key], newData)
          } else {
            break
          }
        }
      } catch (e) {
        console.log(e)
      }
    } else {
      try {
        let i = 0
        for (; i < arr.size; i += 1) {
          const key = arr.get(i)
          const crtData = allData.getIn([parentKey, 'children', key])
          const combin = crtData.get('combin')

          if (combin !== 'follow') {
            break
          }
        }

        if (i < arr.size) {
          const baseData = allData.getIn([parentKey, 'children', arr.get(i)])
          const baseImages = baseData.getIn(fontImagePath)

          for (let j = i - 1; j >= 0; j -= 1) {
            const crtData = allData.getIn([parentKey, 'children', arr.get(j)])
            const newData = crtData.setIn(fontImagePath, baseImages)

            onChange([parentKey, 'children', arr.get(j)], newData)
          }

          const crtData = allData.getIn([parentKey, 'children', childKey])
          const newData = crtData.setIn(fontImagePath, baseImages)
          onChange([parentKey, 'children', childKey], newData)
        }
      } catch (e) {
        console.log(e)
      }
    }
  }

  handleAlreadyImageChange = (key) => {
    const { allAssets, data, onChange, childKey, dialType, parentKey, onAssetsChange } = this.props
    const { uploadedImages = Immutable.Map() } = immutableSelector(allAssets)
    const keyPath = (key || '').split('@_@')
    const images = key ? uploadedImages.getIn([...keyPath, 'images']) : Immutable.List()
    const newData = data.setIn(
      ['text', 'image', 'font_images'],
      Immutable.fromJS([
        {
          language: 'all',
          images
        }
      ])
    )
    let newAllAssets = allAssets

    newAllAssets = newAllAssets.setIn([dialType, parentKey, 'children', childKey, 'alreadyImageKey'], key)

    onChange([parentKey, 'children', childKey], newData)
    onAssetsChange([], newAllAssets)

    this.updatePreview(newData, newAllAssets)
  }

  handleCropImageNameChange = (name) => {
    const { onAssetsChange, parentKey, childKey, assets, dialType } = this.props
    const defaultKey = `${dialType}@_@${parentKey}-${childKey}`
    const alreadyImageKey = assets.get('alreadyImageKey') || defaultKey
    const path = alreadyImageKey.split('@_@')

    onAssetsChange(['uploadedImages', ...path, 'name'], name)
  }

  renderAlreadyImage = () => {
    const { allAssets = Immutable.Map(), assets, dialType } = this.props
    const { uploadedImages = Immutable.Map() } = immutableSelector(allAssets)
    const alreadyImageKey = assets.get('alreadyImageKey') || ''
    const path = alreadyImageKey.split('@_@')
    const alreadyImageName = (uploadedImages || Immutable.Map()).getIn([...path, 'name'])
    const className = alreadyImageKey ? 'item item-clear-padding-bottom' : 'item'
    const isNormal = dialType === 'normal'
    let hasAlreadyImageKey = false

    const renderOptions = (dialType) => {
      const data = uploadedImages.get(dialType) || Immutable.Map()
      const options = data.filter((item) => (item.get('images') || Immutable.List()).size).keySeq()

      return options.map((key) => {
        const imagesKey = `${dialType}@_@${key}`
        if (imagesKey === alreadyImageKey) hasAlreadyImageKey = true
        const path = [dialType, key]
        const imagesName = uploadedImages.getIn([...path, 'name'])

        return (
          <Option key={imagesKey} value={imagesKey}>
            {imagesName}
          </Option>
        )
      })
    }

    const normalOptions = renderOptions('normal')
    const idleOptions = renderOptions('idle')
    const optGroup = []

    if (normalOptions.size) {
      optGroup.push(
        <OptGroup label={<T id="normal" />} key="normal">
          {normalOptions}
        </OptGroup>
      )
    }

    if (idleOptions.size) {
      optGroup.push(
        <OptGroup label={<T id="idle" />} key="idle">
          {idleOptions}
        </OptGroup>
      )
    }

    const order = isNormal ? [0, 1] : [1, 0]

    return (
      <>
        <div className="name">
          <T id="uploaded_image" />
        </div>
        <div className="content">
          <div className={className}>
            <Select
              dropdownClassName="watch-skin-select-dropdown"
              style={SELECT_STYLE}
              value={hasAlreadyImageKey ? alreadyImageKey : alreadyImageName}
              allowClear={!!alreadyImageKey}
              onChange={this.handleAlreadyImageChange}
            >
              {order.map((idx) => optGroup[idx])}
            </Select>
          </div>
        </div>
      </>
    )
  }

  getShowValueMax = (valueCount, stepCount) => {
    if (valueCount) {
      const decimal = +(0.1 ** stepCount).toFixed(stepCount)
      return stepCount ? 10 ** (valueCount - stepCount - 1) - decimal : 10 ** valueCount - 1
    }
    return Infinity
  }

  getIsCharacter = (combin) => {
    const { parentKey, childKey, data = Immutable.Map(), allData = Immutable.Map(), constMap = Immutable.Map() } = this.props
    const isCharacterPath = ['text', 'is_character']
    const is_character = data.getIn(isCharacterPath) || false

    if (combin === 'single') return is_character

    const before = constMap.getIn([childKey, 'before']) || Immutable.List()

    if (!before.size) return is_character

    let i = 0
    for (; i < before.size; i += 1) {
      const beforeKey = before.get(i)
      const beforeCombin = allData.getIn([parentKey, 'children', beforeKey, 'combin'])

      if (beforeCombin && beforeCombin !== 'follow') break
    }

    if (i < before.size) {
      const beforeKey = before.get(i)

      return allData.getIn([parentKey, 'children', beforeKey, ...isCharacterPath])
    }

    return is_character
  }

  getFollowingData = (combin = 'single', path = [], isAssets, defaultValue) => {
    const {
      parentKey,
      childKey,
      assets = Immutable.Map(),
      data = Immutable.Map(),
      allData = Immutable.Map(),
      allAssets = Immutable.Map(),
      constMap = Immutable.Map()
    } = this.props

    const dataSource = isAssets ? assets : data
    const allDataSource = isAssets ? allAssets : allData
    const pathData = dataSource.getIn(path, defaultValue)

    if (combin === 'single') return pathData

    const before = constMap.getIn([childKey, 'before']) || Immutable.List()

    if (!before.size) return pathData

    let i = 0
    for (; i < before.size; i += 1) {
      const beforeKey = before.get(i)
      const beforeCombin = allData.getIn([parentKey, 'children', beforeKey, 'combin'])

      if (beforeCombin && beforeCombin !== 'follow') break
    }

    if (i < before.size) {
      const beforeKey = before.get(i)

      return allDataSource.getIn([parentKey, 'children', beforeKey, ...path], defaultValue)
    }

    return pathData
  }

  handleChangeNegativeImage = (path, prop, img) => {
    if (img) {
      this.handleChange(
        path,
        prop,
        false,
        Immutable.Map({
          url: img.image,
          ...img
        })
      )
    } else {
      this.handleChange(
        path,
        prop,
        false,
        Immutable.Map({
          url: ''
        })
      )
    }
  }

  handleLangChange = (key, value) => {
    const { parentKey, childKey, dialType, allAssets, data, onChange } = this.props
    let previewLang = ''
    let newData = data

    if (key === 'languages') {
      const font_images_path = ['text', 'image', 'font_images']
      newData = data.setIn(font_images_path, Immutable.List())

      onChange([parentKey, 'children', childKey], newData)
      this.changeAssets('previewLang', '')
    }

    if (key === 'previewLang') {
      previewLang = value
    }

    const newAllAssets = allAssets.setIn([dialType, parentKey, 'children', childKey, 'previewLang'], previewLang)

    this.updatePreview(newData, newAllAssets)
    this.changeAssets(key, value)
  }

  handleSepChange = (path, prop, value) => {
    const { onChange = noop, data, childKey, parentKey } = this.props
    const newData = data.setIn([...path, prop], value)
    const rootPath = [parentKey, 'children']

    onChange([...rootPath, childKey], newData)
  }

  handleChangeSepImage = (showSeparatorPos, img) => {
    if (showSeparatorPos) {
      const { parentKey, childKey, data, onChange = noop } = this.props
      const { image = '', width = 0, height = 0 } = img || {}
      const imgPath = ['separator', 'image']
      const newData = data.setIn(imgPath, Immutable.fromJS({ url: image, height, width }))

      onChange([parentKey, 'children', childKey], newData)
    } else {
      this.handleChangeImage(['separator'], 'image', false, img)
    }
  }

  renderUploadByLang = (languages) => {
    const { watchConfig, data } = this.props
    const { screenWidth, screenHeight } = immutableSelector(watchConfig)
    const fontImages = data.getIn(['text', 'image', 'font_images']) || Immutable.List()

    return languages.split(',').map((lang) => {
      const { label, field } = LANG_MAPPER[lang] || {}
      const imagesObj = fontImages.find((images) => images.get('language') === field) || Immutable.Map()
      const images = imagesObj
        .get('images', Immutable.List())
        .toJS()
        .map(({ url, height, width, name }) => ({ image: url, height, width, name }))
      const key = `${languages}-${lang}`

      return (
        <div key={key}>
          <div className="name">{label}</div>
          <div className="content">
            <div className="item">
              <Upload
                maxWidth={screenWidth}
                maxHeight={screenHeight}
                fileList={images}
                field={field}
                multiple
                onChange={(...args) => this.handlePunchChangeImage(false, ...args)}
              />
            </div>
          </div>
        </div>
      )
    })
  }

  renderMultipleLangPic() {
    const { assets, metas } = this.props
    const originLang = getOriginLang(metas)
    const { languages = originLang } = immutableSelector(assets)
    return (
      <div>
        <div className="name">
          <T id="image" />
        </div>
        <div className="content">
          {originLang === languages ? null : (
            <div className="item">
              <Select
                dropdownClassName="watch-skin-select-dropdown"
                style={SELECT_STYLE}
                value={languages}
                disabled
                onChange={(value) => this.handleLangChange('languages', value)}
              >
                <Option value="all">
                  <T id="lang_all" />
                </Option>
                <Option value="zh,zh-Hant,en">
                  <T id="lang_zh" />、<T id="lang_zh-hant" />、<T id="lang_en" />
                </Option>
                <Option value="sc,en">
                  <T id="lang_zh&zh-hant" />、<T id="lang_en" />
                </Option>
              </Select>
            </div>
          )}
        </div>
        {this.renderUploadByLang(languages)}
      </div>
    )
  }

  renderRect = () => {
    const { watchConfig, data } = this.props
    const { screenWidth, screenHeight } = immutableSelector(watchConfig)
    const rectangle = data.getIn(['text', 'image', 'rectangle']) || Immutable.Map()
    const { position = Immutable.Map(), width = 1, height = 1 } = immutableSelector(rectangle)
    const { x = 0, y = 0 } = immutableSelector(position || Immutable.Map())
    const rectPath = ['text', 'image', 'rectangle']

    return (
      <>
        <div className="name">
          <T id="position" />
        </div>
        <div className="content">
          <div className="item">
            <div className="col">
              <div className="key">x</div>
              <InputNumber
                min={0}
                max={screenWidth}
                value={x}
                onChange={(value) => this.handleChange([...rectPath, 'position'], 'x', false, value)}
              />
            </div>
            <div className="col">
              <div className="key">y</div>
              <InputNumber
                min={0}
                max={screenHeight}
                value={y}
                onChange={(value) => this.handleChange([...rectPath, 'position'], 'y', false, value)}
              />
            </div>
          </div>
        </div>
        <div className="name">
          <T id="area_size" />
        </div>
        <div className="content">
          <div className="item">
            <div className="col">
              <div className="key">W</div>
              <InputNumber min={1} max={screenWidth} value={width} onChange={(value) => this.handleChange(rectPath, 'width', false, value)} />
            </div>
            <div className="col">
              <div className="key">H</div>
              <InputNumber min={1} max={screenHeight} value={height} onChange={(value) => this.handleChange(rectPath, 'height', false, value)} />
            </div>
          </div>
        </div>
      </>
    )
  }

  render() {
    const {
      allAssets = Immutable.Map(),
      allData = Immutable.Map(),
      parentKey,
      childKey,
      data,
      dialType,
      namePath,
      constMap = Immutable.Map(),
      hiddenCombin,
      assets = Immutable.Map(),
      hasPointImage,
      watchConfig,
      renderLayerByLang,
      previewUseName = false,
      invalidImageName = <T id="invalid_data" />,
      hideUnitImage = false,
      hideSeparatorImage = false,
      hasPaddingZero = false
    } = this.props

    const { screenWidth, screenHeight } = immutableSelector(watchConfig)
    const name = constMap.getIn([childKey, 'name']) || ''
    const previewMaxValue = constMap.getIn([childKey, 'previewMaxValue'])
    const stepCount = constMap.getIn([childKey, 'stepCount'])
    const { combin = 'single', text = Immutable.Map(), separator = Immutable.Map() } = immutableSelector(data)
    const is_character = this.getFollowingData(combin, ['text', 'is_character'], false, false)
    const supportImage = constMap.getIn([childKey, 'supportImage'])
    const isImage = !!(is_character && supportImage)
    const valueCount = isImage ? 1 : constMap.getIn([childKey, 'valueCount'])
    const hasInvalidImage = constMap.getIn([childKey, 'hasInvalidImage'], true)
    const invalidImageRequired = constMap.getIn([childKey, 'invalidImageRequired'], false)
    const value = assets.getIn(['num', 'value']) || (isImage ? 1 : 0)
    const showValueMax = this.getShowValueMax(valueCount, stepCount)
    const useImage = assets.get('useImage') || 'new'
    const disableFollow = assets.get('disableFollow') || false
    const { uploadedImages = Immutable.Map() } = immutableSelector(allAssets)
    const { rect_align = Immutable.Map(), image = Immutable.Map(), interval = 0, padding_zero } = immutableSelector(text)
    const { horizontal = 'left', vertical = 'top' } = immutableSelector(rect_align)
    const { image: sepImage, position: sepPosition } = immutableSelector(separator)
    const { url: sepUrl } = immutableSelector(sepImage)
    const { x: sepX, y: sepY } = immutableSelector(sepPosition)
    const {
      font_images: fontImages = Immutable.List(),
      unit_images: unitImages = Immutable.List(),
      point_image: pointImage = Immutable.Map(),
      negative_image = Immutable.Map()
    } = immutableSelector(image)
    const negativeImage = (negative_image || Immutable.Map()).get('url')
    const { image: unitImage = Immutable.Map() } = immutableSelector((unitImages || Immutable.List()).get(0, Immutable.Map()))
    const { url: unitUrl } = immutableSelector(unitImage)
    const { url: pointUrl } = immutableSelector(pointImage)
    const fontImageArr = this.getFontImageArr(fontImages)
    const firstKey = constMap.keySeq().get(0)
    const invalidImagePath = ['text', 'image', 'invalid_image']
    const invalidImage = data.getIn(invalidImagePath, '')
    const alreadyImageKey = assets.get('alreadyImageKey') || ''
    const path = alreadyImageKey ? alreadyImageKey.split('@_@') : [dialType, `${parentKey}-${childKey}`]
    const cropImageName = (uploadedImages || Immutable.Map()).getIn([...path, 'name']) || namePath
    const languages = this.getFollowingData(combin, ['languages'], true, 'all')
    const defaultLang = getDefaultLangField(languages, LANG_MAPPER)
    const previewLang = renderLayerByLang || assets.get('previewLang') || defaultLang
    const previewSuffix = is_character && supportImage ? constMap.getIn([childKey, 'previewSuffix']) || '' : ''
    const currentLangImages = fontImages.find((images) => images.get('language') === previewLang) || Immutable.Map()
    const currentLangImagesNames = previewLang ? (currentLangImages.get('images') || Immutable.List()).map((item) => item.get('name')).toJS() : []
    const showRemoveIcon = useImage === 'new' || alreadyImageKey === `${dialType}@_@${parentKey}-${childKey}`
    const showSeparatorPos = TemperatureNumberWithRect.judgeShowSeparatorPos(allData, parentKey, childKey, constMap)

    return (
      <>
        {hiddenCombin ? null : (
          <>
            <div className="name">
              <T id="primary_secondary_relationship" />
            </div>
            <div className="content">
              <div className="item">
                <Select
                  dropdownClassName="watch-skin-select-dropdown"
                  value={combin}
                  style={SELECT_STYLE}
                  onChange={(value) => this.handleChange([], 'combin', false, value)}
                >
                  <Option value="single">
                    <T id="independent" />
                  </Option>
                  <Option value="follow" disabled={disableFollow || firstKey === childKey}>
                    <T id="follow_lowest_temperature" />
                  </Option>
                </Select>
              </div>
            </div>
          </>
        )}
        {invalidImageRequired || hasInvalidImage ? (
          <>
            <div className={invalidImageRequired ? 'name is-required' : 'name'}>{invalidImageName}</div>
            <div className="content">
              <div className="item">
                <div className="col col-v">
                  <Upload
                    className="item"
                    fileCountLimit={1}
                    maxWidth={screenWidth}
                    maxHeight={screenHeight}
                    fileList={invalidImage ? [{ image: invalidImage }] : []}
                    onChange={(image) => this.handleChangeInvalidImage(['text', 'image'], 'invalid_image', false, image)}
                  />
                  <T id="upload_image" />
                </div>
              </div>
            </div>
          </>
        ) : null}
        {combin === 'single' && supportImage ? (
          <>
            <div className="name">
              <T id="display_method" />
            </div>
            <div className="content">
              <div className="item">
                <Select
                  dropdownClassName="watch-skin-select-dropdown"
                  value={is_character ? 1 : 0}
                  style={SELECT_STYLE}
                  onChange={(value) => this.handleChange(['text'], 'is_character', false, value)}
                >
                  <Option value={0} disabled={parentKey === 'weather'}>
                    <T id="number" />
                  </Option>
                  <Option value={1}>
                    <T id="image" />
                  </Option>
                </Select>
              </div>
            </div>
          </>
        ) : null}
        <div className="name is-required">
          <T id="negative_image" />
        </div>
        <div className="content">
          <div className="item">
            <Upload
              key={negativeImage}
              className="item"
              fileCountLimit={1}
              maxWidth={screenWidth}
              maxHeight={screenHeight}
              fileList={negativeImage ? [{ image: negativeImage }] : []}
              help={
                <div style={{ textAlign: 'center' }}>
                  <T id="negative_image" />
                </div>
              }
              onChange={(image) => this.handleChangeNegativeImage(['text', 'image'], 'negative_image', image)}
            />
          </div>
        </div>
        {combin === undefined || combin === 'single' ? (
          <>
            {!is_character ? (
              <>
                <div className="name">
                  <T id="number_slice" />
                </div>
                <div className="content">
                  <div className="item">
                    <Radio.Group value={useImage} onChange={this.handleRadioChange}>
                      <Radio style={RADIO_STYLE} value="already">
                        <T id="use_uplod_img" />
                      </Radio>
                      <Radio style={RADIO_STYLE} value="new">
                        <T id="use_new_img" />
                      </Radio>
                    </Radio.Group>
                  </div>
                </div>
                <>
                  {useImage === 'new' ? (
                    <div className="name is-required">
                      <T id="upload_image" />
                    </div>
                  ) : (
                    this.renderAlreadyImage()
                  )}
                  {useImage === 'new' || fontImageArr.length > 0 ? (
                    <div className="content">
                      <div className="item">
                        <Upload
                          key={useImage === 'new' ? useImage : alreadyImageKey}
                          multiple
                          fileCountLimit={supportImage ? Infinity : 10}
                          maxWidth={screenWidth}
                          maxHeight={screenHeight}
                          fileList={fontImageArr}
                          field="all"
                          onChange={(...args) => this.handlePunchChangeImage(true, ...args)}
                          defineCropImageName
                          cropImageName={cropImageName}
                          showRemoveIcon={showRemoveIcon}
                          handleCropImageNameChange={this.handleCropImageNameChange}
                        />
                      </div>
                    </div>
                  ) : null}
                </>
              </>
            ) : null}
            {this.renderRect()}
            {isImage && this.renderMultipleLangPic()}
            {isImage ? null : (
              <>
                <div className="name">
                  <T id="alignment" />
                </div>
                <div className="content">
                  <div className="item">
                    <Select
                      dropdownClassName="watch-skin-select-dropdown"
                      value={horizontal}
                      style={SELECT_STYLE}
                      onChange={(value) => this.handleChange(['text', 'rect_align'], 'horizontal', false, value)}
                    >
                      <Option value={ALIGN_H.LEFT}>
                        <T id="align_left" />
                      </Option>
                      <Option value={ALIGN_H.CENTER}>
                        <T id="horizontal_center" />
                      </Option>
                      <Option value={ALIGN_H.RIGHT}>
                        <T id="align_right" />
                      </Option>
                    </Select>
                  </div>
                  <div className="item">
                    <Select
                      dropdownClassName="watch-skin-select-dropdown"
                      value={vertical}
                      style={SELECT_STYLE}
                      onChange={(value) => this.handleChange(['text', 'rect_align'], 'vertical', false, value)}
                    >
                      <Option value={ALIGN_V.TOP}>
                        <T id="align_top" />
                      </Option>
                      <Option value={ALIGN_V.CENTER}>
                        <T id="vertical_center" />
                      </Option>
                      <Option value={ALIGN_V.BOTTOM}>
                        <T id="align_bottom" />
                      </Option>
                    </Select>
                  </div>
                </div>
              </>
            )}
            {isImage ? null : (
              <>
                <div className="name">
                  <T id="space" />
                </div>
                <div className="content">
                  <div className="item">
                    <InputNumber
                      min={0}
                      value={interval}
                      style={SELECT_STYLE}
                      onChange={(value) => this.handleChange(['text'], 'interval', false, value)}
                    />
                  </div>
                </div>
              </>
            )}
          </>
        ) : null}
        {isImage || !hasPaddingZero ? null : (
          <>
            <div className="name">
              <T id="format" />
            </div>
            <div className="content">
              <div className="item" onClick={(e) => this.handleCheckChange(['text'], 'padding_zero', false, padding_zero, e)}>
                <Radio checked={padding_zero} style={RADIO_STYLE}>
                  <T id="whether_padding_zero" />
                </Radio>
              </div>
            </div>
          </>
        )}
        {hideUnitImage ? null : (
          <>
            <div className="name">
              <T id="unit_image" />
            </div>
            <div className="content">
              <div className="item">
                <div className="col col-v">
                  <Upload
                    key={childKey}
                    fileCountLimit={1}
                    maxWidth={screenWidth}
                    maxHeight={screenHeight}
                    fileList={unitUrl ? [{ image: unitUrl }] : []}
                    help={
                      <div style={{ textAlign: 'center' }}>
                        <T id="upload_image" />
                      </div>
                    }
                    onChange={(image) => this.handleChangeImage(['text', 'image', 'unit_images', 0], 'image', false, image)}
                  />
                </div>
              </div>
            </div>
          </>
        )}
        {hasPointImage ? (
          <>
            <div className="name is-required">
              <T id="point_image" />
            </div>
            <div className="content">
              <div className="item">
                <div className="col col-v">
                  <Upload
                    fileCountLimit={1}
                    maxWidth={screenWidth}
                    maxHeight={screenHeight}
                    fileList={pointUrl ? [{ image: pointUrl }] : []}
                    help={
                      <div style={{ textAlign: 'center' }}>
                        <T id="upload_image" />
                      </div>
                    }
                    onChange={(image) => this.handleChangeImage(['text', 'image'], 'point_image', false, image)}
                  />
                </div>
              </div>
            </div>
          </>
        ) : null}
        {hideSeparatorImage ? null : (
          <>
            <div className="name">
              <T id="data_type_image" />
            </div>
            <div className="content">
              <div className="item">
                <Upload
                  fileCountLimit={1}
                  maxWidth={screenWidth}
                  maxHeight={screenHeight}
                  fileList={sepUrl ? [{ image: sepUrl }] : []}
                  help={
                    <div style={{ textAlign: 'center' }}>
                      <T id="upload_image" />
                    </div>
                  }
                  onChange={(image) => this.handleChangeSepImage(showSeparatorPos, image)}
                />
              </div>
            </div>
            {showSeparatorPos ? (
              <>
                <div className="content">
                  <div className="item">
                    <div className="col">
                      <div className="key">x</div>
                      <InputNumber
                        min={0}
                        max={screenWidth}
                        value={sepX}
                        onChange={(value) => this.handleSepChange(['separator', 'position'], 'x', value)}
                      />
                    </div>
                    <div className="col">
                      <div className="key">y</div>
                      <InputNumber
                        min={0}
                        max={screenHeight}
                        value={sepY}
                        onChange={(value) => this.handleSepChange(['separator', 'position'], 'y', value)}
                      />
                    </div>
                  </div>
                </div>
              </>
            ) : null}
          </>
        )}
        {isImage ? (
          <div className="content">
            <div className="item">
              <T id="preview_lang" />
            </div>
            <Select
              dropdownClassName="watch-skin-select-dropdown"
              style={SELECT_STYLE}
              value={previewLang}
              onChange={(value) => this.handleLangChange('previewLang', value)}
            >
              {languages.split(',').map((lang) => {
                const { label, field } = LANG_MAPPER[lang] || {}

                return (
                  <Option key={field} value={field}>
                    {label}
                  </Option>
                )
              })}
            </Select>
          </div>
        ) : null}
        <div className="content">
          <div className="item">
            <T id="preview_with_name" values={{ name: <T id={name} /> }} />
          </div>
          {previewUseName ? (
            <Select
              dropdownClassName="watch-skin-select-dropdown"
              style={SELECT_STYLE}
              defaultValue={currentLangImagesNames[0]}
              onChange={(value) => this.handleChange(['num'], 'value', true, value)}
            >
              {currentLangImagesNames.map((name, index) => (
                <Option key={name} value={index + 1}>
                  {name}
                </Option>
              ))}
            </Select>
          ) : (
            <InputNumber
              min={-999}
              max={previewMaxValue || showValueMax}
              value={value}
              step={stepCount ? (0.1 ** stepCount).toFixed(stepCount) : 1}
              precision={stepCount || 0}
              style={{ width: 325 }}
              formatter={(value) => `${value}${previewSuffix}`}
              parser={(value = '') => value.replace(previewSuffix, '')}
              onChange={(value) => this.handleChange(['num'], 'value', true, value)}
            />
          )}
        </div>
      </>
    )
  }
}

export default TemperatureNumberWithRect
